import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, CardHeader, Typography, TextField, Grid, Chip } from '@mui/material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompany } from '../../../redux/company/types';
import { Add } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useDispatch } from 'react-redux';
import { updateCompany } from '../../../redux/company/actions';
import { RichTextEditor } from '../../Common/RichTextEditor';

interface ICompanyAboutEdit {
    company?: ICompany;
    isEditing: boolean;
    onSave: (saveFunction: () => void) => void;
    onChanges: (hasChanges: boolean) => void;
}

const CompanyAboutEdit: React.FC<ICompanyAboutEdit> = ({ company, isEditing, onSave, onChanges }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        about: company?.about || '',
        perks: company?.perks || [],
        values: company?.values || [],
    });
    const [initialData, setInitialData] = useState(formData);
    const [newPerk, setNewPerk] = useState('');
    const [newValue, setNewValue] = useState('');

    useEffect(() => {
        if (company) {
            const newData = {
                about: company.about || '',
                perks: company.perks || [],
                values: company.values || [],
            };
            setFormData(newData);
            setInitialData(newData);
        }
    }, [company]);

    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialData);
        onChanges(hasChanges);
    }, [formData, initialData, onChanges]);

    const handleFieldChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const addPerk = () => {
        if (newPerk.trim() && !formData.perks.includes(newPerk.trim())) {
            handleFieldChange('perks', [...formData.perks, newPerk.trim()]);
            setNewPerk('');
        }
    };

    const removePerk = (perkToRemove: string) => {
        handleFieldChange(
            'perks',
            formData.perks.filter(perk => perk !== perkToRemove),
        );
    };

    const addValue = () => {
        if (newValue.trim() && !formData.values.includes(newValue.trim())) {
            handleFieldChange('values', [...formData.values, newValue.trim()]);
            setNewValue('');
        }
    };

    const removeValue = (valueToRemove: string) => {
        handleFieldChange(
            'values',
            formData.values.filter(value => value !== valueToRemove),
        );
    };

    const handleSave = () => {
        if (company?._id) {
            const payload = {
                id: company._id,
                body: {
                    about: formData.about,
                    perks: formData.perks,
                    values: formData.values,
                },
            };
            dispatch(updateCompany(payload));
        }
    };

    // Register the save function with the parent container
    useEffect(() => {
        onSave(handleSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave, formData, company?._id]);

    return (
        <Card sx={{ bgcolor: theme[themeStyle].secondaryBg, border: `1px solid ${theme[themeStyle].tag_white_light}` }}>
            <CardHeader
                title="About & Overview"
                sx={{ color: theme[themeStyle].tag_white }}
                titleTypographyProps={{
                    sx: { fontFamily: FONT_ENUM.MAIN_BOLD },
                }}
            />
            <CardContent>
                <Grid container spacing={3}>
                    {/* About */}
                    <Grid item xs={12}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                mb: 2,
                            }}>
                            About
                        </Typography>
                        <RichTextEditor
                            value={formData.about}
                            onChange={value => {
                                handleFieldChange('about', value);
                            }}
                        />
                    </Grid>

                    {/* Perks */}
                    <Grid item xs={12}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                mb: 2,
                            }}>
                            Company Perks
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                            {formData.perks.map(perk => (
                                <Chip
                                    key={perk}
                                    label={perk}
                                    onDelete={isEditing ? () => removePerk(perk) : undefined}
                                    sx={{
                                        backgroundColor: theme[themeStyle].highlight,
                                        color: theme[themeStyle].black,
                                        '& .MuiChip-deleteIcon': {
                                            color: theme[themeStyle].black,
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                        {isEditing && (
                            <Box display="flex" gap={1}>
                                <TextField
                                    value={newPerk}
                                    onChange={e => setNewPerk(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && addPerk()}
                                    label="Add Perk"
                                    InputLabelProps={{
                                        sx: {
                                            color: theme[themeStyle].grey,
                                        },
                                    }}
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].grey,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}
                                    InputProps={{
                                        style: {
                                            background: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].lightWhite,
                                            borderRadius: 8,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            border: `1px solid ${theme[themeStyle].form_gray}`,
                                        },
                                    }}
                                />
                                <IconButton onClick={addPerk}>
                                    <Add sx={{ color: theme[themeStyle].tag_white }} />
                                </IconButton>
                            </Box>
                        )}
                    </Grid>

                    {/* Values */}
                    <Grid item xs={12}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                mb: 2,
                            }}>
                            Company Values
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                            {formData.values.map(value => (
                                <Chip
                                    key={value}
                                    label={value}
                                    onDelete={isEditing ? () => removeValue(value) : undefined}
                                    sx={{
                                        backgroundColor: theme[themeStyle].highlight,
                                        color: theme[themeStyle].black,
                                        '& .MuiChip-deleteIcon': {
                                            color: theme[themeStyle].black,
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                        {isEditing && (
                            <Box display="flex" gap={1}>
                                <TextField
                                    value={newValue}
                                    onChange={e => setNewValue(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && addValue()}
                                    label="Add Value"
                                    InputLabelProps={{
                                        sx: {
                                            color: theme[themeStyle].grey,
                                        },
                                    }}
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].grey,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}
                                    InputProps={{
                                        style: {
                                            background: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].lightWhite,
                                            borderRadius: 8,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            border: `1px solid ${theme[themeStyle].form_gray}`,
                                        },
                                    }}
                                />
                                <IconButton onClick={addValue}>
                                    <Add sx={{ color: theme[themeStyle].tag_white }} />
                                </IconButton>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export { CompanyAboutEdit };
